import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Col,
    Dropdown,
    DropdownButton,
    Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { Sight, StudentGradeNoSeatBirth, UserInfo, WH } from '../../../model';
import apis from '../../../apis';
import { SightContext } from '../../TableHeaders/SightHeader';
import {
    downloadDataAsExcel,
    printDataAsTable,
    SheetHeaderDisplay,
} from '../../../utils';
import { sightDiag, whbmiDiag } from '../../../utils/sight';
import { Nullable } from '../../../types';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
type PageData = StudentGradeNoSeatBirth & Nullable<Sight> & Nullable<WH> & { diag: string } & { sightdiag: string } & { overW: string } & { fitbmistr: string } & { fitweightstr: string } & { agerange: number };
function boolRender(v: unknown) {
    return v ? '是' : '否';
}
const manageIDMap: Record<string, string> = {
    '1': '長效散瞳劑\n(阿托平 Atropine)',
    '2': '短效散瞳劑',
    '3': '其他藥物',
    '4': '配鏡矯治',
    '5': '更換鏡片',
    '6': '遮眼治療',
    '7': '配戴隱形眼鏡',
    '8': '角膜塑型片',
    '9': '視力保健衛教',
    '10': '其他',
    '11': '定期檢查',
};
const formatSightValue = (value: unknown) => {
    if (typeof value !== 'number') return value as string;

    if (value === -1) return '<0.1';
    if (value < -1) return String(value);

    return Number.isInteger(value) ? value.toFixed(1) : String(value);
};
const tableHeader: (SheetHeaderDisplay<PageData> &
    HeaderDisplayKeysWithType<PageData>)[] = [
        { display: '統編', property: 'pid' },
        { display: '學號', property: 'sid' },
        { display: '年級', property: 'grade' },
        {
            display: '班級',
            property: 'no',
            onRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return value > 100 ? value - 100 : value;
                }
                return value;
            },
            onSheetRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return value > 100 ? value - 100 : value;
                }
                return value as string;
            }
        },
        { display: '座號', property: 'seat' },
        { display: '姓名', property: 'name' },
        {
            display: '性別',
            property: 'sex',
            onRender: (v) => (v === '1' ? '男' : '女'),
            onSheetRender: (v) => (v === '1' ? '男' : '女'),
        },
        {
            display: '生日',
            property: 'birth',
            onRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : null;
            },
            onSheetRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : "";
            },
        },
        {
            display: '生日(民國年)',
            property: 'birth',
            onRender: (v) => {
                if (!(v instanceof DateTime)) return null;
                const rocYear = v.year - 1911;
                const mm = v.toFormat('MM');
                const dd = v.toFormat('dd');
                return `${rocYear}${mm}${dd}`;
            },
            onSheetRender: (v) => {
                if (!(v instanceof DateTime)) return '';
                const rocYear = v.year - 1911;
                const mm = v.toFormat('MM');
                const dd = v.toFormat('dd');
                return `${rocYear}${mm}${dd}`;
            },
        },
        {
            display: '年齡',
            property: 'agerange',
        },
        { display: '身高不足標準', property: 'fitHeight' },
        { display: '身高', property: 'height' },
        { display: '體重', property: 'weight' },
        {
            display: '測量日',
            property: 'examDate',
            onRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : v;
            },
            onSheetRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : "";
            },
        },
        { display: '體位', property: 'diag' },
        { display: '建議體重', property: 'fitweightstr' },
        {
            display: '過重量',
            property: 'overW',
            onRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return Math.round(value * 10) / 10; // 四捨五入到小數點後一位
                }
                return value;
            },
            onSheetRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return Math.round(value * 10) / 10; // 四捨五入到小數點後一位
                }
                return value as string;
            },
        },
        {
            display: 'BMI',
            property: 'bmi',
            onRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return Math.round(value * 10) / 10; // 四捨五入到小數點後一位
                }
                return value;
            },
            onSheetRender: (value: unknown) => {
                if (typeof value === 'number') {
                    return Math.round(value * 10) / 10; // 四捨五入到小數點後一位
                }
                return value as string;
            },
        },
        {
            display: '建議BMI',
            property: 'fitbmistr',

        },
        {
            display: '散瞳治療',
            property: 'isDilated',
            onRender: boolRender,
            onSheetRender: boolRender,
        },
        {
            display: '裸視右',
            property: 'sight0R',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '裸視左',
            property: 'sight0L',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '戴鏡右',
            property: 'sightR',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '戴鏡左',
            property: 'sightL',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '散瞳',
            property: 'isDilating',
            onRender: boolRender,
            onSheetRender: boolRender,
        },
        { display: '近視右', property: 'eNearR' },
        { display: '近視左', property: 'eNearL' },
        { display: '遠視右', property: 'eFarR' },
        { display: '遠視左', property: 'eFarL' },
        { display: '散光右', property: 'eSanR' },
        { display: '散光左', property: 'eSanL' },
        { display: '屈光右', property: 'diopR' },
        { display: '屈光左', property: 'diopL' },
        { display: '診斷', property: 'sightdiag' },
        { display: '備註', property: 'eSight99State' },
        {
            display: '處置代號',
            property: 'sightManageCodes',
            onRender: (v) => (Array.isArray(v) ? v.join(',') : v),
            onSheetRender: (v) => (Array.isArray(v) ? v.join(',') : (v as string)),
        },
        {
            display: '醫師建議處置',
            property: 'sightManageCodes',
            onRender: (v) => {
                if (Array.isArray(v)) {
                    return v.map(id => manageIDMap[id] || '').join(', ');
                }
                return '';
            },
            onSheetRender: (v) => {
                if (Array.isArray(v)) {
                    return v.map(id => manageIDMap[id] || '').join(', ');
                }
                return '';
            },
        },
        {
            display: '定期檢查', property: 'periodical',
            onRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : null;
            },
            onSheetRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : "";
            },
        },
    ];

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const wHSchoolSight: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const { yearSem, element: semGradeElement } = useSemGrade();
    const [students, setStudents] = useState<PageData[]>([]);
    const [userinfo, setUser] = useState<UserInfo>();
    useEffect(() => {
        if (yearSem) {
            apis.getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(catchErrorForModal);

            toast
                .promise(
                    apis
                        .getWHClassSight(yearSem.year, yearSem.sem)
                        .then(r => {
                            setStudents(
                                r.map(({ sight, wh, ...s }) => ({
                                    ...sight,
                                    ...wh,
                                    ...s,
                                    diag: whbmiDiag(wh?.bmiCode?.toString()),
                                    sightdiag: sightDiag(sight ?? undefined),
                                    overW: (wh?.bmiCode == "1" || wh?.bmiCode == "2") ? ((wh?.weight ?? 0) - (s.fitWeight ?? 0)).toFixed(1) : "",
                                    fitbmistr: (wh?.bmiCode ?? "-1") != "0" ? (s.fitBMI ?? 0).toString() : "",
                                    fitweightstr: (wh?.bmiCode ?? "-1") != "0" ? (s.fitWeight ?? 0).toString() : "",
                                    agerange: calculateAge(s.birth, wh?.examDate ?? DateTime.now())
                                }))
                            )
                        }),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
                .catch(catchErrorForModal);

        }
    }, [yearSem?.year, yearSem?.sem]);
    console.log(students)
    return (
        <AuthedLayout>
            <Row className="justify-content-between">
                <Col>
                    <Row>
                        <Col xs={3} className="mr-3">
                            {semGradeElement}
                        </Col>
                    </Row>
                </Col>
                <div>
                    <DropdownButton
                        id="wh-school-sight-export"
                        title="下載 / 列印"
                        variant="info"
                        className="text-dark"
                        disabled={!students.length}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                downloadDataAsExcel({
                                    title: `${user.schools[user.currentSchool].name}${yearSem?.year}學年${yearSem?.sem}學期_全校身高體重視力`,
                                    values: students,
                                    headers: tableHeader,
                                    context: SightContext,
                                    footer:
                                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                    exportfile: userinfo?.exportFileType,
                                });
                            }}
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                printDataAsTable({
                                    title: `${user.schools[user.currentSchool].name}${yearSem?.year}學年${yearSem?.sem}學期_全校身高體重視力`,
                                    values: students,
                                    headers: tableHeader,
                                    context: SightContext,
                                    footer:
                                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                });
                            }}
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
            </Row>
            <hr />
            {/* <Row>
                <EditableTableDiv
                    headers={tableHeader}
                    values={students}
                    context={SightContext}
                />
            </Row> */}
        </AuthedLayout>
    );
};

function calculateAge(birthDate: DateTime, checkDate: DateTime): number {
    if (!checkDate) return -1;

    const birth = new Date(birthDate.year, birthDate.month - 1, birthDate.day);
    const check = new Date(checkDate.year, checkDate.month - 1, checkDate.day);

    const startOfCheckYear = new Date(check.getFullYear(), 0, 1);
    const startOfBirthYear = new Date(birth.getFullYear(), 0, 1);

    const daysSince1911Check =
        (check.getFullYear() - 1911 - 1) * 365 +
        (Math.floor((check.getTime() - startOfCheckYear.getTime()) / (1000 * 60 * 60 * 24)) + 1);

    const daysSince1911Birth =
        (birth.getFullYear() - 1911 - 1) * 365 +
        (Math.floor((birth.getTime() - startOfBirthYear.getTime()) / (1000 * 60 * 60 * 24)) + 1);

    const diffDays = daysSince1911Check - daysSince1911Birth;
    const ageFloat = diffDays / 365;

    let finalAge: number;
    const decimal = ageFloat - Math.floor(ageFloat);

    if (decimal >= 0.75) {
        finalAge = Math.floor(ageFloat) + 1;
    } else if (decimal >= 0.25) {
        finalAge = Math.floor(ageFloat) + 0.5;
    } else {
        finalAge = Math.floor(ageFloat);
    }

    return finalAge;
}

export const WHSchoolSight = connector(wHSchoolSight);